import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../../components/AppIcon";
import Button from "../../../../components/ui/Button";
import AiGeneratedBadge from "../../../../components/ui/AiGeneratedBadge";
import {
  CoverageRecommendationView,
  CoverageUnavailableReason,
  getCoverageRecommendations,
} from "../../../../api/General/assets";
import { EquipmentPriority } from "../../../../api/General/businessRoles";
import { priorityToneClass } from '@/components/assets/equipmentRequirementChrome';

interface CoverageAiPanelProps {
  employeeId: string;
  employeeName?: string;
}

// The server returns a bare English verb. Translate the ones we know, show the rest verbatim
// rather than dropping guidance we simply have no label for.
const actionLabelKey: Record<string, string> = {
  Issue: "assets.coverage.ai.actionIssue",
  Procure: "assets.coverage.ai.actionProcure",
  Defer: "assets.coverage.ai.actionDefer",
  Replace: "assets.coverage.ai.actionReplace",
  Review: "assets.coverage.ai.actionReview",
};

const CoverageAiPanel: React.FC<CoverageAiPanelProps> = ({ employeeId, employeeName }) => {
  const { t } = useTranslation();
  const [result, setResult] = useState<CoverageRecommendationView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Nothing to analyse is a settled answer, not a retry: asking again returns the same thing.
  const nothingToAnalyse = result?.reason === CoverageUnavailableReason.NoRequirements;

  const handleGenerate = async () => {
    if (loading || nothingToAnalyse) return;
    setLoading(true);
    setError("");
    try {
      const response = await getCoverageRecommendations(employeeId);
      if (response.errors.length > 0 || !response.payload) {
        setError(response.errors[0]?.description || t("assets.coverage.ai.failed"));
        return;
      }
      setResult(response.payload);
    } catch {
      setError(t("assets.coverage.ai.failed"));
    } finally {
      setLoading(false);
    }
  };

  const hasRecommendations = !!result && result.recommendations.length > 0;

  return (
    <div
      className="space-y-3 mt-5 pt-4 border-t border-border"
      data-testid={`coverage-ai-${employeeId}`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("assets.coverage.ai.heading")}
          </h4>
          {/* Provenance, not decoration: without a key these come from a rule, and saying so matters. */}
          {result && <AiGeneratedBadge visible={result.aiAvailable} />}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleGenerate}
          disabled={loading || nothingToAnalyse}
          loading={loading}
          data-testid={`coverage-ai-generate-${employeeId}`}
        >
          {result
            ? t("assets.coverage.ai.regenerate")
            : t("assets.coverage.ai.generate")}
        </Button>
      </div>

      {!result && !error && (
        <p className="text-xs text-muted-foreground">
          {employeeName
            ? t("assets.coverage.ai.introNamed", { name: employeeName })
            : t("assets.coverage.ai.intro")}
        </p>
      )}

      {error && (
        <p className="text-xs text-destructive" data-testid={`coverage-ai-error-${employeeId}`}>
          {error}
        </p>
      )}

      {result && (
        <div className="space-y-3">
          <p
            className="text-sm text-foreground"
            data-testid={`coverage-ai-summary-${employeeId}`}
          >
            {result.summary}
          </p>

          {/* One line covering both provenance and caution. The rule-based path still produces
              real advice, so label its source rather than presenting it as a failure. */}
          {hasRecommendations && (
            <p className="text-xs text-muted-foreground" data-testid={`coverage-ai-disclaimer-${employeeId}`}>
              {result.aiAvailable
                ? t("assets.coverage.ai.disclaimer")
                : t("assets.coverage.ai.disclaimerFallback")}
            </p>
          )}

          {hasRecommendations && (
            <ul className="space-y-2" data-testid={`coverage-ai-list-${employeeId}`}>
              {result.recommendations.map((rec, i) => (
                <li
                  key={`${rec.category}-${rec.action}-${i}`}
                  className="border border-border rounded-md p-3 bg-background"
                >
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {/* The verb is the point of the row, so it leads. */}
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${priorityToneClass[rec.priority] ?? priorityToneClass[EquipmentPriority.Optional]}`}
                    >
                      {actionLabelKey[rec.action] ? t(actionLabelKey[rec.action]) : rec.action}
                    </span>
                    <span className="font-medium text-foreground text-sm">{rec.category}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{rec.detail}</p>
                </li>
              ))}
            </ul>
          )}

          {!hasRecommendations && !nothingToAnalyse && (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Icon name="Check" size={14} className="text-success" aria-hidden="true" />
              {t("assets.coverage.ai.noActions")}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default CoverageAiPanel;
